import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import Footer from '../components/Footer';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  // Get the payment_intent and payment_intent_client_secret from URL
  const paymentIntentId = searchParams.get('payment_intent');
  const clientSecret = searchParams.get('payment_intent_client_secret');
  const redirectStatus = searchParams.get('redirect_status');
  const serverId = searchParams.get('server_id');

  // Function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    // Verify the payment status
    const verifyPayment = async () => {
      try {
        if (!paymentIntentId || !clientSecret) {
          setStatus('error');
          return;
        }

        // If we have redirect_status and it's 'succeeded', we can show success immediately
        if (redirectStatus === 'succeeded') {
          setStatus('success');
          setPaymentDetails({
            id: paymentIntentId,
            amount: '£1.00',
            date: new Date().toLocaleDateString(),
            serverId: serverId || 'Not provided'
          });
          return;
        }

        // Otherwise, verify with the Cloudflare Worker
        const response = await fetch(`https://overseer-payment-worker.yourdomain.workers.dev/verify-payment?payment_intent=${paymentIntentId}`);
        const data = await response.json();

        // For local development, you can use this URL instead:
        // const response = await fetch(`http://localhost:3001/verify-payment?payment_intent=${paymentIntentId}`);

        if (data.success) {
          setStatus('success');
          setPaymentDetails({
            id: data.paymentIntent.id,
            amount: `£${(data.paymentIntent.amount / 100).toFixed(2)}`,
            date: new Date(data.paymentIntent.created * 1000).toLocaleDateString(),
            serverId: serverId || 'Not provided'
          });
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
      }
    };

    verifyPayment();
  }, [paymentIntentId, clientSecret, redirectStatus, serverId]);

  return (
    <div>
      <div className="container-custom py-24">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-8 text-center"
          >
            {status === 'loading' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-custom-400 mx-auto mb-4"></div>
                <h1 className="text-2xl font-semibold text-gray-custom-100 mb-4">
                  Verifying your payment...
                </h1>
                <p className="text-gray-custom-400">
                  Please wait while we confirm your payment details.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="relative">
                  <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gray-custom-800 rounded-full p-4 border-4 border-gray-custom-900">
                      <CheckCircleIcon className="h-16 w-16 text-green-500" />
                    </div>
                  </div>
                </div>

                <h1 className="text-3xl font-bold text-gray-custom-100 mt-10 mb-4">
                  Premium Activated!
                </h1>
                <p className="text-gray-custom-400 mb-8 max-w-lg mx-auto">
                  Thank you for upgrading to Premium! Your payment has been processed successfully and your server now has access to all premium features.
                </p>

                {paymentDetails && (
                  <div className="bg-gray-custom-800/50 border border-gray-custom-700 rounded-lg p-6 mb-8 max-w-md mx-auto">
                    <h3 className="text-lg font-medium text-gray-custom-100 mb-4 flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 mr-2 text-green-500" />
                      Premium Details
                    </h3>

                    <div className="space-y-4">
                      <div className="flex justify-between items-center pb-2 border-b border-gray-custom-700">
                        <span className="text-gray-custom-400">Server ID:</span>
                        <div className="flex items-center">
                          <span className="text-gray-custom-100 mr-2 font-mono">{paymentDetails.serverId}</span>
                          <button
                            onClick={() => copyToClipboard(paymentDetails.serverId)}
                            className="text-gray-custom-400 hover:text-white transition-colors"
                            title="Copy to clipboard"
                          >
                            {copied ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pb-2 border-b border-gray-custom-700">
                        <span className="text-gray-custom-400">Plan:</span>
                        <span className="text-gray-custom-100">Lifetime Premium</span>
                      </div>

                      <div className="flex justify-between items-center pb-2 border-b border-gray-custom-700">
                        <span className="text-gray-custom-400">Amount:</span>
                        <span className="text-gray-custom-100">{paymentDetails.amount}</span>
                      </div>

                      <div className="flex justify-between items-center pb-2 border-b border-gray-custom-700">
                        <span className="text-gray-custom-400">Date:</span>
                        <span className="text-gray-custom-100">{paymentDetails.date}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-gray-custom-400">Payment ID:</span>
                        <span className="text-gray-custom-100 text-xs font-mono truncate max-w-[150px]" title={paymentDetails.id}>
                          {paymentDetails.id}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-custom-800/30 border border-gray-custom-700 rounded-lg p-4 mb-8 max-w-md mx-auto">
                  <h3 className="text-lg font-medium text-gray-custom-100 mb-2 flex items-center">
                    <ServerIcon className="h-5 w-5 mr-2 text-gray-custom-400" />
                    Next Steps
                  </h3>
                  <ul className="text-sm text-gray-custom-400 space-y-2 mt-3">
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">1</span>
                      <span>Make sure Overseer bot is added to your Discord server</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">2</span>
                      <span>Use the <code className="bg-gray-custom-800 px-1 py-0.5 rounded text-gray-custom-200">!premium activate</code> command in your server</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">3</span>
                      <span>Enjoy all premium features immediately!</span>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/" className="btn btn-secondary">
                    Return to Home
                  </Link>
                  <a
                    href="https://discord.com/oauth2/authorize?client_id=YOUR_CLIENT_ID&scope=bot&permissions=8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Add to Discord
                  </a>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="relative">
                  <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gray-custom-800 rounded-full p-4 border-4 border-gray-custom-900">
                      <XCircleIcon className="h-16 w-16 text-red-500" />
                    </div>
                  </div>
                </div>

                <h1 className="text-3xl font-bold text-gray-custom-100 mt-10 mb-4">
                  Payment Verification Failed
                </h1>
                <p className="text-gray-custom-400 mb-8 max-w-lg mx-auto">
                  We couldn't verify your payment. This could be due to a temporary issue or because the payment was declined.
                </p>

                <div className="bg-gray-custom-800/30 border border-gray-custom-700 rounded-lg p-6 mb-8 max-w-md mx-auto">
                  <h3 className="text-lg font-medium text-gray-custom-100 mb-4">
                    Possible Reasons
                  </h3>
                  <ul className="text-sm text-gray-custom-400 space-y-3">
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">•</span>
                      <span>Your payment was declined by your bank or card issuer</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">•</span>
                      <span>There was a temporary issue with our payment processor</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-custom-700 text-xs text-white mr-2 mt-0.5">•</span>
                      <span>The payment verification process timed out</span>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/premium" className="btn btn-secondary flex items-center justify-center">
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Try Again
                  </Link>
                  <a
                    href="https://discord.gg/overseer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Contact Support
                  </a>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default PaymentSuccess;
